export type InstrumentId =
  | 'meter-rule'
  | 'vernier-0.1'
  | 'vernier-0.05'
  | 'vernier-0.02'
  | 'micrometer'
  | 'measuring-cylinder-10'
  | 'measuring-cylinder-25'
  | 'measuring-cylinder-50'
  | 'measuring-cylinder-100'
  | 'measuring-cylinder-250'
  | 'measuring-cylinder-500'
  | 'measuring-cylinder-1000'
  | 'burette-50'
  | 'pipette-25'
  | 'thermometer-analog'
  | 'thermometer-digital'
  | 'stopwatch-analog'
  | 'stopwatch-digital'
  | 'beam-balance'
  | 'electronic-balance-0.01'
  | 'electronic-balance-0.001'
  | 'spring-balance'
  | 'protractor'
  | 'trundle-wheel';

export type MeasurementQuantity = 'length' | 'volume' | 'temperature' | 'time' | 'mass' | 'force' | 'angle';

export interface InstrumentMetadata {
  id: InstrumentId;
  name: string;
  quantity: MeasurementQuantity;
  range: { min: number; max: number };
  unit: string;
  leastCount: number;
  uncertainty: number;
  graduations: string;
  scale: 'linear' | 'vernier' | 'circular' | 'digital' | 'meniscus' | 'balance';
  zeroErrorSupported: boolean;
  parallaxSupported: boolean;
  invertedScale?: boolean;
  fixedVolume?: number;
  measurementModes?: Array<'external' | 'internal' | 'depth'>;
}

const instrument = (
  id: InstrumentId,
  name: string,
  quantity: MeasurementQuantity,
  min: number,
  max: number,
  unit: string,
  leastCount: number,
  uncertainty: number,
  graduations: string,
  scale: InstrumentMetadata['scale'],
  extras: Partial<InstrumentMetadata> = {},
): InstrumentMetadata => ({
  id, name, quantity, range: { min, max }, unit, leastCount, uncertainty, graduations,
  scale, zeroErrorSupported: true, parallaxSupported: scale !== 'digital', ...extras,
});

/** Instrument specifications for the WAEC measurement practical. */
export const instruments: Record<InstrumentId, InstrumentMetadata> = {
  'meter-rule': instrument('meter-rule', 'Metre rule', 'length', 0, 1, 'm', 0.001, 0.0005, '1 mm divisions', 'linear'),
  'vernier-0.1': instrument('vernier-0.1', 'Vernier calipers (0.1 mm)', 'length', 0, 150, 'mm', 0.1, 0.05, 'Main scale 1 mm; vernier least count 0.1 mm', 'vernier', { measurementModes: ['external', 'internal', 'depth'] }),
  'vernier-0.05': instrument('vernier-0.05', 'Vernier calipers (0.05 mm)', 'length', 0, 150, 'mm', 0.05, 0.025, 'Main scale 1 mm; vernier least count 0.05 mm', 'vernier', { measurementModes: ['external', 'internal', 'depth'] }),
  'vernier-0.02': instrument('vernier-0.02', 'Vernier calipers (0.02 mm)', 'length', 0, 150, 'mm', 0.02, 0.01, 'Main scale 1 mm; vernier least count 0.02 mm', 'vernier', { measurementModes: ['external', 'internal', 'depth'] }),
  micrometer: instrument('micrometer', 'Micrometer screw gauge', 'length', 0, 25, 'mm', 0.01, 0.005, 'Sleeve 0.5 mm; thimble 0.01 mm divisions', 'circular'),
  'measuring-cylinder-10': instrument('measuring-cylinder-10', 'Measuring cylinder (10 cm3)', 'volume', 0, 10, 'cm3', 0.2, 0.1, '0.2 cm3 divisions; read bottom of meniscus', 'meniscus'),
  'measuring-cylinder-25': instrument('measuring-cylinder-25', 'Measuring cylinder (25 cm3)', 'volume', 0, 25, 'cm3', 0.5, 0.25, '0.5 cm3 divisions; read bottom of meniscus', 'meniscus'),
  'measuring-cylinder-50': instrument('measuring-cylinder-50', 'Measuring cylinder (50 cm3)', 'volume', 0, 50, 'cm3', 1, 0.5, '1 cm3 divisions; read bottom of meniscus', 'meniscus'),
  'measuring-cylinder-100': instrument('measuring-cylinder-100', 'Measuring cylinder (100 cm3)', 'volume', 0, 100, 'cm3', 1, 0.5, '1 cm3 divisions; read bottom of meniscus', 'meniscus'),
  'measuring-cylinder-250': instrument('measuring-cylinder-250', 'Measuring cylinder (250 cm3)', 'volume', 0, 250, 'cm3', 2, 1, '2 cm3 divisions; read bottom of meniscus', 'meniscus'),
  'measuring-cylinder-500': instrument('measuring-cylinder-500', 'Measuring cylinder (500 cm3)', 'volume', 0, 500, 'cm3', 5, 2.5, '5 cm3 divisions; read bottom of meniscus', 'meniscus'),
  'measuring-cylinder-1000': instrument('measuring-cylinder-1000', 'Measuring cylinder (1000 cm3)', 'volume', 0, 1000, 'cm3', 10, 5, '10 cm3 divisions; read bottom of meniscus', 'meniscus'),
  'burette-50': instrument('burette-50', 'Burette (50 cm3)', 'volume', 0, 50, 'cm3', 0.1, 0.05, '0.1 cm3 divisions; zero at top and scale increases downwards', 'meniscus', { invertedScale: true }),
  'pipette-25': instrument('pipette-25', 'Volumetric pipette (25 cm3)', 'volume', 0, 25, 'cm3', 0.1, 0.06, 'Single 25 cm3 calibration line; use a pipette filler', 'meniscus', { fixedVolume: 25 }),
  'thermometer-analog': instrument('thermometer-analog', 'Laboratory thermometer', 'temperature', -10, 110, 'degC', 1, 0.5, '1 degC divisions', 'linear'),
  'thermometer-digital': instrument('thermometer-digital', 'Digital thermometer', 'temperature', -10, 110, 'degC', 0.1, 0.1, '0.1 degC display resolution', 'digital'),
  'stopwatch-analog': instrument('stopwatch-analog', 'Stopwatch (analog)', 'time', 0, 3600, 's', 0.1, 0.2, '0.1 s divisions; include reaction-time uncertainty', 'circular'),
  'stopwatch-digital': instrument('stopwatch-digital', 'Stopwatch (digital)', 'time', 0, 3600, 's', 0.01, 0.2, '0.01 s display; include reaction-time uncertainty', 'digital'),
  'beam-balance': instrument('beam-balance', 'Beam balance', 'mass', 0, 500, 'g', 0.1, 0.05, 'Rider divisions of 0.1 g', 'balance'),
  'electronic-balance-0.01': instrument('electronic-balance-0.01', 'Electronic balance (0.01 g)', 'mass', 0, 5000, 'g', 0.01, 0.005, '0.01 g display resolution', 'digital'),
  'electronic-balance-0.001': instrument('electronic-balance-0.001', 'Electronic balance (0.001 g)', 'mass', 0, 500, 'g', 0.001, 0.0005, '0.001 g display resolution', 'digital'),
  'spring-balance': instrument('spring-balance', 'Spring balance', 'force', 0, 10, 'N', 0.1, 0.05, '0.1 N divisions', 'linear'),
  protractor: instrument('protractor', 'Protractor', 'angle', 0, 180, 'deg', 1, 0.5, '1 degree divisions', 'circular'),
  'trundle-wheel': instrument('trundle-wheel', 'Trundle wheel', 'length', 0, 1000, 'm', 0.01, 0.005, '0.01 m counter increment', 'digital'),
};

export function getInstrument(id: InstrumentId | string): InstrumentMetadata {
  if (!Object.prototype.hasOwnProperty.call(instruments, id)) {
    throw new RangeError(`Unknown measurement instrument: ${id}`);
  }
  return instruments[id as InstrumentId];
}

export function listInstruments(): InstrumentMetadata[] {
  return Object.values(instruments);
}